'use client';

import { KeyRound, LogOut, Menu, PenLine, User } from 'lucide-react';
import type { Sesion } from './Login';
import CampanaAvisos from './CampanaAvisos';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { iniciales } from '@/lib/utils';
import { labelRol } from '@/lib/roles';
import { estiloAvatarRol } from '@/lib/permisos';
import { ROLES_PARTE_UI } from '@/lib/menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Barra superior: título del sistema + datos del usuario en sesión + salir.
// El menú del avatar también da acceso a "Mi firma" (antes vivía en el Sidebar;
// se movió aquí por ser una acción de cuenta personal, no un módulo del proceso).
export default function Topbar({
  usuario,
  onLogout,
  onNavegar,
  onAbrirExpediente,
  onAbrirMenu,
}: {
  usuario: Sesion;
  onLogout: () => void;
  onNavegar?: (v: string) => void;
  onAbrirExpediente?: (id: string) => void;
  onAbrirMenu?: () => void;
}) {
  // Las partes del portal (disciplinado/defensor) no gestionan firma propia.
  const tieneFirma = !ROLES_PARTE_UI.includes(usuario.rol);

  return (
    <header className="flex h-16 items-center justify-between gap-2 border-b bg-card px-4 sm:px-6">
      <div className="flex min-w-0 items-center gap-1.5">
        {/* Hamburguesa: abre el cajón del menú en pantallas pequeñas. */}
        {onAbrirMenu && (
          <Button
            variant="ghost" size="icon" className="lg:hidden"
            onClick={onAbrirMenu} aria-label="Abrir menú"
          >
            <Menu className="size-5" />
          </Button>
        )}
        {/* Escudo del Departamento de Risaralda en el header (antes no tenía logo). */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/assets/img/Escudo_de_Risaralda.svg"
          alt="Escudo del Departamento de Risaralda"
          className="h-9 w-auto shrink-0"
        />
        <span className="truncate text-sm font-medium text-muted-foreground">
          Sistema de Gestión Disciplinaria
        </span>
      </div>

      <div className="flex items-center gap-3">
        <div className="hidden text-right sm:block">
          <div className="text-sm font-medium leading-none">{usuario.nombre}</div>
          <div className="mt-1 text-xs text-muted-foreground">{labelRol(usuario.rol)}</div>
        </div>

        <CampanaAvisos onAbrirExpediente={onAbrirExpediente} onNavegar={onNavegar} />

        <Separator orientation="vertical" className="hidden h-8 sm:block" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full">
              <Avatar className="size-9">
                {/* Avatar teñido con el color propio del rol (matriz central). */}
                <AvatarFallback style={estiloAvatarRol(usuario.rol)}>
                  {iniciales(usuario.nombre)}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex items-center gap-2">
                <User className="size-4" />
                <div>
                  <div className="text-sm font-medium">{usuario.nombre}</div>
                  <div className="text-xs font-normal text-muted-foreground">
                    {labelRol(usuario.rol)}
                  </div>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {tieneFirma && (
              <DropdownMenuItem onClick={() => onNavegar?.('mi-firma')}>
                <PenLine className="size-4" />
                Mi firma
              </DropdownMenuItem>
            )}
            {/* Cambiar la propia contraseña: disponible para TODO usuario autenticado. */}
            <DropdownMenuItem onClick={() => onNavegar?.('mi-contrasena')}>
              <KeyRound className="size-4" />
              Cambiar contraseña
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onLogout}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="size-4" />
              Salir
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
